/**
 * Helper functions to fetch 3D model data for products
 */

/**
 * Fetches detailed product data including media info
 * @param {string} productHandle - The product handle
 * @returns {Promise<object>} - Promise resolving to detailed product data
 */
export async function getProductDetails(productHandle) {
  try {
    const response = await fetch(`/products/${productHandle}.js`);
    const product = await response.json();
    return product;
  } catch (error) {
    console.error(`Error fetching detailed product data for ${productHandle}:`, error);
    return null;
  }
}

/**
 * Extract 3D model info from product data
 * @param {object} product - The product data
 * @returns {object} - Object containing model3dUrl and model3dPreviewImage if found
 */
export function extract3DModelInfo(product) {
  let modelInfo = {
    model3dUrl: null,
    model3dPreviewImage: null
  };
  
  if (!product) return modelInfo;
  
  // Check if product has media array
  if (product.media && Array.isArray(product.media)) {
    
    // Look for 3D model in product media
    const model3dMedia = product.media.find(media => {
      return media.media_type === 'model' || 
             media.media_type === '3d' || 
             (media.alt && media.alt.toLowerCase().includes('3d model'));
    });
    
    if (model3dMedia) {

      modelInfo.model3dUrl = model3dMedia.src || model3dMedia.sources?.[1]?.url;
      modelInfo.model3dPreviewImage = model3dMedia.preview_image?.src || 
                                     (product.images && product.images.length > 0 ? product.images[0].src : null);
    }
  }
  
  // Alternative: check for 3D model URL in metafields (if available)
  if (!modelInfo.model3dUrl && product.metafields) {
    
    // Look for metafields that might contain 3D model information
    const modelMetafield = 
      product.metafields?.model_3d || 
      product.metafields?.model3d || 
      product.metafields?.model_url;
      
    if (modelMetafield) {
      modelInfo.model3dUrl = modelMetafield;
      modelInfo.model3dPreviewImage = product.images[0]?.src;
    }
  }
  
  // Alternative: Check for 3D model URL in tags with a specific format
  // For example: "3d-model:https://example.com/model.glb"
  if (!modelInfo.model3dUrl && product.tags && Array.isArray(product.tags)) {
    
    const modelTag = product.tags.find(tag => 
      tag.toLowerCase().startsWith('3d-model:') || 
      tag.toLowerCase().startsWith('model-url:')
    );
    
    if (modelTag) {
      const urlPart = modelTag.split(':')[1];
      if (urlPart && urlPart.trim()) {
        modelInfo.model3dUrl = urlPart.trim();
        modelInfo.model3dPreviewImage = product.images[0]?.src;
      }
    }
  }
  
  // Last resort: Look for hints in product description
  if (!modelInfo.model3dUrl && product.body_html) {
    const modelUrlMatch = product.body_html.match(/3[dD]\s*[mM]odel\s*URL\s*:\s*(https?:\/\/[^\s"'<>]+)/i);
    
    if (modelUrlMatch && modelUrlMatch[1]) {
      modelInfo.model3dUrl = modelUrlMatch[1];
      modelInfo.model3dPreviewImage = product.images[0]?.src;
    }
  }
  
  return modelInfo;
}
